import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { refreshSession } from './net/api.js';
import { useSessionStore } from './state/session-store.js';
import { AnnouncementBanner } from './ui/AnnouncementBanner.js';
import { BirthdayBanner } from './ui/BirthdayBanner.js';
import { ChatPanel } from './ui/ChatPanel.js';
import { ControlsTutorial } from './ui/ControlsTutorial.js';
import { FirstLogin } from './ui/FirstLogin.js';
import { GameCanvas } from './ui/GameCanvas.js';
import { Hud, LanguageSwitch } from './ui/Hud.js';
import { DmPanel } from './ui/DmPanel.js';
import { MapChangeBanner } from './ui/MapChangeBanner.js';
import { Lobby } from './ui/Lobby.js';
import { LoginScreen } from './ui/LoginScreen.js';
import { QueueScreen } from './ui/QueueScreen.js';
import { ReconnectBanner } from './ui/ReconnectBanner.js';
import { TravelMap } from './ui/TravelMap.js';
import { WelcomeSplash } from './ui/WelcomeSplash.js';

/** Reads and removes `?authError=` that the server's OAuth callback may have added. */
function takeAuthError(): string | undefined {
  const url = new URL(window.location.href);
  const error = url.searchParams.get('authError') ?? undefined;
  if (error) {
    url.searchParams.delete('authError');
    window.history.replaceState(null, '', url.toString());
  }
  return error;
}

const authError = takeAuthError();

export function App() {
  const { t, i18n } = useTranslation();
  const auth = useSessionStore((s) => s.auth);
  const user = useSessionStore((s) => s.user);
  const room = useSessionStore((s) => s.room);
  const status = useSessionStore((s) => s.status);
  // Arriving in the other office (a teleport) shows the welcome splash again (ADR-0037).
  const arrivals = useSessionStore((s) => s.arrivals);
  const travelling = useSessionStore((s) => s.travel !== undefined);

  useEffect(() => {
    void refreshSession().then((session) => {
      const store = useSessionStore.getState();
      if (session) store.setSignedIn(session.user);
      else store.setSignedOut();
    });
  }, []);

  if (auth === 'unknown') {
    return <div className="app centered notice">{t('connection.connecting')}</div>;
  }

  if (auth === 'signed-out' || !user) {
    return (
      <Shell>
        <LoginScreen authError={authError} />
      </Shell>
    );
  }

  // The canvas stays mounted while a dropped connection is being resumed.
  if ((status === 'connected' || status === 'reconnecting') && room) {
    return (
      <div className="app">
        <GameCanvas room={room} />
        <Hud />
        <AnnouncementBanner />
        <BirthdayBanner />
        <MapChangeBanner />
        <ReconnectBanner />
        <TravelMap />
        {!travelling && <WelcomeSplash key={arrivals} />}
        <ChatPanel />
        <DmPanel />
        <ControlsTutorial />
      </div>
    );
  }

  if (status === 'queued') {
    return (
      <Shell language={{ current: i18n.language, label: t('hud.language') }}>
        <QueueScreen />
      </Shell>
    );
  }

  return <Shell>{user.appearance ? <Lobby user={user} /> : <FirstLogin user={user} />}</Shell>;
}

/** Centered screen; the corner language switch is only for screens that have no Settings panel. */
function Shell({
  children,
  language,
}: {
  children: React.ReactNode;
  language?: { current: string; label: string };
}) {
  return (
    <div className="app centered">
      {children}
      {language && (
        <div className="corner">
          <LanguageSwitch current={language.current} label={language.label} />
        </div>
      )}
    </div>
  );
}
